import { getFunctions, httpsCallable } from 'firebase/functions';
import { app } from '../firebase/fire';

/**
 * Sends email notification to admin when a new member signs up
 * Uses Firebase Cloud Function with Nodemailer
 */
export const notifyAdminNewSignup = async (memberData) => {
  try {
    // Always try HTTP endpoint first (has explicit CORS support for all domains)
    // This works for localhost, office.littlepower.club, and any other domain
    console.log('Using HTTP endpoint for email notification');
    return await sendViaHTTP(memberData);

  } catch (error) {
    console.error('Error in notifyAdminNewSignup:', error);
    
    // If HTTP fails and we're in production (not localhost), try callable as fallback
    const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    
    if (!isDevelopment) {
      console.warn('HTTP failed in production, trying callable fallback...');
      try {
        return await sendViaCallable(memberData);
      } catch (callableError) {
        console.error('Callable fallback also failed:', callableError);
        return { 
          success: false, 
          error: 'Failed to send notification via both methods',
          originalError: error.message
        };
      }
    }
    
    // Don't fail signup if notification fails
    return { 
      success: false, 
      error: error.message || 'Failed to send notification',
      code: error.code
    };
  }
};

/**
 * Send via HTTP endpoint with explicit CORS handling
 */
async function sendViaHTTP(memberData) {
  try {
    const response = await fetch(
      'https://us-central1-little-power.cloudfunctions.net/sendAdminNotificationHTTP',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          memberName: memberData.name,
          memberLastName: memberData.lastName,
          memberEmail: memberData.email,
          memberPhone: memberData.phone
        })
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      console.error('HTTP response error:', response.status, errorData);
      return {
        success: false,
        error: errorData.error || `HTTP ${response.status}: Failed to send notification`
      };
    }

    const result = await response.json();
    console.log('Email sent via HTTP:', result);
    
    return {
      success: result.success,
      data: result
    };

  } catch (error) {
    console.error('HTTP fetch error:', error);
    throw error;
  }
}

/**
 * Send via Firebase Callable (production)
 */
async function sendViaCallable(memberData) {
  try {
    const functions = getFunctions(app);
    
    const sendAdminNotification = httpsCallable(functions, 'sendAdminNotification', {
      timeout: 30000
    });
    
    const result = await sendAdminNotification({
      memberName: memberData.name,
      memberLastName: memberData.lastName,
      memberEmail: memberData.email,
      memberPhone: memberData.phone
    });

    if (result.data && result.data.success) {
      console.log('Admin notification sent via Cloud Function:', result.data);
      return { 
        success: true, 
        data: result.data 
      };
    } else if (result.data && !result.data.success) {
      console.warn('Cloud Function returned error:', result.data.error);
      return { 
        success: false, 
        error: result.data.error || 'Failed to send notification',
        data: result.data
      };
    } else {
      console.warn('Unexpected Cloud Function response:', result.data);
      return { 
        success: true,
        data: result.data,
        warning: 'Unexpected response format'
      };
    }

  } catch (error) {
    console.error('Callable error:', error);
    throw error;
  }
}
